import { Fragment, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { headCellOf } from './approvalCells';
import type { JournalForm, MetaField } from './journalForm';
import {
  blankTbm,
  DEFAULT_PATROL_HEADER,
  headerFor,
  isInspection,
  type Activity,
  type Check,
  type CheckResult,
  type EducationSub,
  type Facility,
  type HeadApproval,
  type HealthItem,
  type JournalInput,
  type JournalPhoto,
  type PatrolHeader,
  type PatrolKind,
  type PatrolNote,
  type PhotoSlot,
} from './safeJournalApi';

/**
 * 일지 A4 미리보기 (DOC-122) — 편집 창 오른쪽. 적는 즉시 바뀐다.
 *
 * <p>칸 배치는 {@link JournalForm} 이 정하고, 글자 · 선 · 여백은 서버 PDF 틀({@code SafeJournalDocService})의
 * CSS 를 옮겨 왔다. 빈 줄을 거르는 규칙(성명 없는 참석자 등)도 서버 저장과 같다 — 저장하면 그 줄은 사라진다.
 *
 * <p>쪽 나눔은 그리지 않는다. 한 장을 넘는 일지는 아래로 이어 그린다.
 */
export function JournalPaper({
  form,
  journal,
  siteName,
  kindLabel,
  photos,
  edit,
  education,
  photoUpload,
  patrolHeader,
  missing,
  approvals,
}: {
  form: JournalForm;
  journal: Omit<JournalInput, 'siteId'>;
  siteName: string;
  /** 교육구분 「대분류 · 소분류」 — 이름은 서버 카탈로그에 있어 부르는 쪽이 지어 준다. */
  kindLabel: string;
  photos: JournalPhoto[];
  /** 종이에 바로 쓰기(DOC-122 §13 · §15). 없으면 읽기 전용. */
  edit?: PaperEdit;
  /** 교육일지를 종이에 쓸 때의 교육구분 · 시간 손잡이. */
  education?: EducationTools;
  /** 안전보건일지 사진대지 자리의 올리기 · 사진내용 칸 — 옛 손잡이. 사진대지가 한 칸이 되며(§31) 쓰지 않는다. */
  slotTools?: SlotTools;
  /** 안전보건일지 사진대지의 [＋ 사진] — 편집 창이 그려 넘긴다(§31). */
  photoUpload?: ReactNode;
  /** 순회점검일지 머리 글(양식 편집 · V540). 없으면 회사 양식의 기본 글. */
  patrolHeader?: PatrolHeader | null;
  /** [저장] 이 막힌 뒤 비어 있는 [필수] 칸 — 빨간 테두리를 친다. */
  missing?: ReadonlySet<RequiredField>;
  /** 결재란 「현장소장」 승인(V548) — 값과 [승인] · [승인 취소] 손잡이. 손잡이가 없으면 찍힌 것만 보인다. */
  approvals?: Approvals;
}) {
  const sections = form.sections.filter((section) => section.key !== 'photos' || photos.length > 0);
  const inkStyles = edit ? <style>{INK_STYLES}</style> : null;

  // 안전보건일지는 엑셀 양식 세 장(안전보건일지 · T.B.M · 사진대지)이다. 그 밖의 사진은 사진대지 장을 더 붙여 싣는다.
  if (journal.journalType === 'HEALTH') {
    return (
      <div style={paper}>
        {inkStyles}
        <HealthSheet
          journal={journal}
          siteName={siteName}
          photos={photos}
          edit={edit}
          photoUpload={photoUpload}
          approvals={approvals}
        />
      </div>
    );
  }

  // 순회 · 합동점검일지는 종이 양식 한 장이 머리 · 점검표 · 특이사항을 다 담는다. 사진만 그 아래에 붙는다.
  if (isInspection(journal.journalType)) {
    return (
      <div style={paper}>
        {inkStyles}
        <PatrolSheet journal={journal} siteName={siteName} edit={edit} header={patrolHeader} approvals={approvals} />
        <PhotoSheets photos={photos} siteName={siteName} />
      </div>
    );
  }

  if (journal.journalType === 'EDUCATION') {
    return (
      <div style={paper}>
        {inkStyles}
        <EducationSheet
          journal={journal}
          photos={photos.slice(0, EDU_CELL_PHOTOS)}
          edit={edit}
          tools={education}
          missing={missing}
          approvals={approvals}
        />
        <PhotoSheets photos={photos.slice(EDU_CELL_PHOTOS)} siteName={siteName} />
      </div>
    );
  }

  return (
    <div style={paper}>
      <h1 style={heading}>{form.heading}</h1>
      <table style={table}>
        <tbody>
          {form.meta.map(([left, right]) => (
            <tr key={`${left.field}-${right?.field ?? ''}`}>
              <th style={{ ...th, width: '15%' }}>{left.label}</th>
              {right ? (
                <>
                  <td style={{ ...td, width: '35%' }}>{metaValue(left.field, journal, siteName, kindLabel)}</td>
                  <th style={{ ...th, width: '15%' }}>{right.label}</th>
                  <td style={{ ...td, width: '35%' }}>{metaValue(right.field, journal, siteName, kindLabel)}</td>
                </>
              ) : (
                <td style={td} colSpan={3}>
                  {metaValue(left.field, journal, siteName, kindLabel)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {sections.map((section) => {
        if (section.key === 'content') return <Note key="content" label={section.label} text={journal.content || '-'} />;
        if (section.key === 'photos') return <Photos key="photos" label={section.label} photos={photos} />;
        if (section.key === 'patrolNotes') return <PatrolNotes key="patrolNotes" journal={journal} />;
        if (section.key === 'activities') {
          return (
            <Grid
              key="activities"
              label={section.label}
              columns={[
                ['행사 및 활동 (교육, 합동점검, 행사 등)', 50],
                ['안전 공정 회의 내용 (직원 공지사항)', null],
              ]}
              rows={journal.activities
                .filter((row) => row.activity?.trim() || row.meeting?.trim())
                .map((row) => [row.activity ?? '', row.meeting ?? ''])}
            />
          );
        }
        if (section.key === 'facilities') {
          return (
            <Grid
              key="facilities"
              label={section.label}
              columns={[
                ['시설물 종류', 20],
                ['설치장소', 20],
                ['투입인원', 10],
                ['설치내용', null],
              ]}
              centered={[2]}
              rows={journal.facilities
                .filter((row) => row.facilityKind?.trim() || row.location?.trim() || row.detail?.trim())
                .map((row) => [row.facilityKind ?? '', row.location ?? '', row.workers === null ? '' : `${row.workers} 명`, row.detail ?? ''])}
            />
          );
        }
        return <Lines key="lines" form={form} label={section.label} journal={journal} />;
      })}
    </div>
  );
}

/** 소장 · 안전 Patrol — 서버 틀처럼 두 표로. 빈 표도 머리는 그린다. */
function PatrolNotes({ journal }: { journal: Omit<JournalInput, 'siteId'> }) {
  const filled = journal.patrolNotes.filter((row) => row.instruction?.trim() || row.result?.trim());
  return (
    <>
      <Grid
        label="소장 Patrol 점검 현황"
        columns={[
          ['지도, 지시, 점검내용', 50],
          ['조치결과', null],
        ]}
        rows={filled.filter((row) => row.patrolKind === 'HEAD').map((row) => [row.instruction ?? '', row.result ?? ''])}
      />
      <Grid
        label="안전 Patrol 점검 현황"
        columns={[
          ['점검자', 14],
          ['지도, 지시, 점검내용', 43],
          ['조치결과', null],
        ]}
        centered={[0]}
        rows={filled
          .filter((row) => row.patrolKind === 'SAFE')
          .map((row) => [row.inspector ?? '', row.instruction ?? '', row.result ?? ''])}
      />
    </>
  );
}

/** 이름 · 폭(%) 짝의 칸으로 그리는 단순 표. 줄이 없으면 「-」 한 줄. */
function Grid({
  label,
  columns,
  rows,
  centered = [],
}: {
  label: string;
  columns: Array<[string, number | null]>;
  rows: string[][];
  centered?: number[];
}) {
  return (
    <Section label={label}>
      <table style={table}>
        <thead>
          <tr>
            {columns.map(([name, width]) => (
              <th key={name} style={{ ...th, width: width === null ? undefined : `${width}%` }}>
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td style={{ ...td, textAlign: 'center' }} colSpan={columns.length}>
                -
              </td>
            </tr>
          ) : (
            rows.map((row, at) => (
              <tr key={at}>
                {row.map((value, index) => (
                  <td key={index} style={centered.includes(index) ? { ...td, textAlign: 'center' } : td}>
                    {value}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Section>
  );
}

/**
 * 순회점검일지 — 사용자가 준 종이 양식 한 장(2026-10-23 · DOC-122 §11). 서버 틀({@code patrolPaper})과 같은 격자다.
 * 결재란은 확정 규격(대표이사 외 서명칸 30px · 이름표 배경 통일). 같은 구분이 이어지면 구분 칸을 합친다.
 */
function PatrolSheet({
  journal,
  siteName,
  edit,
  header,
  approvals,
}: {
  journal: Omit<JournalInput, 'siteId'>;
  siteName: string;
  edit?: PaperEdit;
  header?: PatrolHeader | null;
  approvals?: Approvals;
}) {
  const h = headerFor(journal.journalType, header ?? DEFAULT_PATROL_HEADER);
  const results = h.resultLabels.length === 3 ? h.resultLabels : DEFAULT_PATROL_HEADER.resultLabels;
  const rows = journal.checks.filter((row) => row.item.trim());
  const mid: CSSProperties = { ...td, verticalAlign: 'middle' };
  const label: CSSProperties = { ...th, verticalAlign: 'middle' };
  /**
   * 결과 칸 — 종이에 바로 쓸 때는 칸을 누르면 ○ 가 찍히고, 찍힌 칸을 다시 누르면 지워진다(셋 중 하나만).
   * 쓰는 사람이 고치는 것은 이 칸과 특이사항뿐이다 — 항목은 양식이다(§14).
   */
  const mark = (index: number, value: CheckResult) => {
    const on = rows[index]!.result === value;
    if (!edit) return <td style={{ ...mid, textAlign: 'center' }}>{on ? '○' : ''}</td>;
    const target = rows[index]!;
    return (
      <td
        role="checkbox"
        aria-checked={on}
        aria-label={`${target.item} ${results[value === 'GOOD' ? 0 : value === 'ACTION' ? 1 : 2]}`}
        tabIndex={0}
        className="sj-mark"
        style={{ ...mid, textAlign: 'center', fontWeight: 700 }}
        onClick={() => toggle(target, value)}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            toggle(target, value);
          }
        }}
      >
        {on ? '○' : ''}
      </td>
    );
  };
  const toggle = (target: Check, value: CheckResult) =>
    edit?.({ checks: journal.checks.map((row) => (row === target ? { ...row, result: row.result === value ? null : value } : row)) });
  return (
    <FillPage>
      {(extra) => (
    <table style={table}>
      <colgroup>
        <col style={{ width: '12%' }} />
        <col />
        <col style={{ width: '9%' }} />
        <col style={{ width: '9%' }} />
        <col style={{ width: '10%' }} />
      </colgroup>
      <tbody>
        <tr>
          <td style={{ ...mid, textAlign: 'center' }} colSpan={2} rowSpan={2}>
            <div style={{ fontSize: '20pt', fontWeight: 700, letterSpacing: '0.15em' }}>{h.title}</div>
            {h.subtitle && <div style={{ fontSize: '10.5pt', marginTop: '4px' }}>{h.subtitle}</div>}
          </td>
          <th style={label} rowSpan={2}>
            결<br />재
          </th>
          {h.signers.length >= 2 ? (
            <>
              <th style={label}>{h.signers[0]}</th>
              <th style={label}>{h.signers[1]}</th>
            </>
          ) : (
            <th style={label} colSpan={2}>
              {h.signers[0] ?? ''}
            </th>
          )}
        </tr>
        <tr>
          {h.signers.length >= 2 ? (
            <>
              <td style={signCell}>
                <HeadSign tools={roleTools(approvals, h.signers, 0)} />
              </td>
              <td style={signCell}>
                <HeadSign tools={roleTools(approvals, h.signers, 1)} />
              </td>
            </>
          ) : (
            <td style={signCell} colSpan={2}>
              <HeadSign tools={approvals?.HEAD} />
            </td>
          )}
        </tr>
        <tr>
          <th style={label}>현장명</th>
          <td style={mid}>{siteName}</td>
          <th style={label}>일시</th>
          <td style={{ ...mid, textAlign: 'center' }} colSpan={2}>
            {journal.journalDate}
          </td>
        </tr>
        <tr>
          <th style={label}>{h.categoryLabel}</th>
          <th style={label}>{h.itemLabel}</th>
          {results.map((name, at) => (
            <th key={at} style={label}>
              {name}
            </th>
          ))}
        </tr>
        {rows.map((row, at) => {
          const category = row.category ?? null;
          const continues = at > 0 && category !== null && category === rows[at - 1]!.category;
          let span = 1;
          while (category !== null && rows[at + span]?.category === category) span++;
          return (
            <tr key={at}>
              {!continues && (
                <td style={{ ...mid, textAlign: 'center' }} rowSpan={span}>
                  {category ?? ''}
                </td>
              )}
              <td style={mid}>· {row.item}</td>
              {mark(at, 'GOOD')}
              {mark(at, 'ACTION')}
              {mark(at, 'NA')}
            </tr>
          );
        })}
        <tr>
          <th style={{ ...label, height: '60px' }}>{h.noteLabel}</th>
          <td style={{ ...td, whiteSpace: 'pre-wrap', height: `${80 + extra}px` }} colSpan={4}>
            <Ink value={journal.content} onChange={edit ? (v) => edit({ content: v === '' ? null : v }) : undefined} multiline minHeight={80} />
          </td>
        </tr>
        {/* 맨 아래 기타 의견(사용자 지시 2026-10-23) — 값은 일지의 specialNote. 서버 patrolPaper 와 같은 자리. */}
        <tr>
          <th style={{ ...label, height: '50px' }}>기타 의견</th>
          <td style={{ ...td, whiteSpace: 'pre-wrap' }} colSpan={4}>
            <Ink value={journal.specialNote} onChange={edit ? (v) => edit({ specialNote: v === '' ? null : v }) : undefined} multiline minHeight={60} />
          </td>
        </tr>
      </tbody>
    </table>
      )}
    </FillPage>
  );
}

function metaValue(field: MetaField, journal: Omit<JournalInput, 'siteId'>, siteName: string, kindLabel: string): string {
  switch (field) {
    case 'site':
      return siteName;
    case 'date':
      return journal.journalDate;
    case 'kind':
      return kindLabel;
    case 'time': {
      const { startTime: start, endTime: end } = journal;
      const range = !start && !end ? null : `${start?.slice(0, 5) ?? ''}${end ? ` ~ ${end.slice(0, 5)}` : ''}`;
      if (journal.journalType !== 'EDUCATION' || journal.educationHours === null) return range ?? '';
      const hours = `${journal.educationHours}시간`;
      return range ? `${range} (${hours})` : hours;
    }
    case 'target':
      return journal.target ?? '';
    case 'headcount':
      return journal.headcount === null ? '' : `${journal.headcount} 명`;
    case 'place':
      return journal.place ?? '';
    case 'instructor':
      return journal.instructor ?? '';
    case 'inspector':
      return journal.inspector ?? '';
  }
}

/** 안전보건일지의 위험요인 · 안전조치 줄 — 서버가 저장할 때 버리는 빈 줄은 여기서도 뺀다. */
function lineRows(journal: Omit<JournalInput, 'siteId'>): string[][] {
  return journal.healthItems
    .filter((row) => row.workContent?.trim() || row.hazard?.trim() || row.measure?.trim())
    .map((row, index) => [
      String(index + 1),
      row.workContent ?? '',
      row.location ?? '',
      row.hazard ?? '',
      row.measure ?? '',
      row.checked ? '확인' : '',
      row.note ?? '',
    ]);
}

function Lines({ form, label, journal }: { form: JournalForm; label: string; journal: Omit<JournalInput, 'siteId'> }) {
  const rows = lineRows(journal);
  const empty = '-';
  // 서버 틀처럼 No · 이행 칸은 가운데.
  const centered = new Set([0, 5]);
  return (
    <Section label={label}>
      <table style={table}>
        <thead>
          <tr>
            {form.columns.map((column) => (
              <th key={column.label} style={{ ...th, width: column.width === null ? undefined : `${column.width}%` }}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td style={{ ...td, textAlign: 'center' }} colSpan={form.columns.length}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row, at) => (
              <tr key={at}>
                {row.map((cell, index) => (
                  <td key={index} style={centered.has(index) ? { ...td, textAlign: 'center' } : td}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Section>
  );
}

function Note({ label, text }: { label: string; text: string }) {
  return (
    <Section label={label}>
      <div style={note}>{text}</div>
    </Section>
  );
}

/** 안전보건일지 사진대지 한 자리의 손잡이 — 올리기 단추와 사진내용 칸. 업로드 로직은 편집 창에 있다. */
export type SlotTools = (slot: PhotoSlot) => { upload: ReactNode; caption: ReactNode };

/** 교육일지 종이에 편집 창이 넘겨 주는 값 — 고른 교육(결재 칸 이름 · 교육 구분 칸)과 양식의 교육 구분 여섯 칸. */
export type EducationTools = {
  sub: EducationSub | null;
  boxes: string[];
  /** 교육 구분 칸을 눌렀을 때 — 그 칸의 교육으로 바꾼다(편집 창의 chooseEducation). */
  choose?: (kind: string, sub: string | null) => void;
  /** 「교육 사진」 칸 위의 올리기 단추 — 편집 창이 그려 넘긴다. */
  photoUpload?: ReactNode;
};

/**
 * 교육 구분 칸 번호 — 카탈로그(box)가 정한다. 서버가 옛 판이라 box 가 없으면 갈래로 정한다(같은 규칙):
 * 1 채용 시 · 2 작업내용 변경 시 · 3 특별 · 4 근로자 정기 · 5 관리감독자 · 6 그 밖(기타 · MSDS · 특수형태 · 위기 · 성희롱 · 장애인).
 */
function boxOf(kind: string | null, sub: string | null, fromCatalog: number | null | undefined): number {
  if (fromCatalog) return fromCatalog;
  if (!kind) return 0;
  if (kind === 'WORKER') return sub === 'HIRE' ? 1 : sub === 'CHANGE' ? 2 : 4;
  if (kind === 'SPECIAL') return 3;
  if (kind === 'SUPERVISOR') return 5;
  return 6;
}

/** 교육 구분 칸을 누르면 고를 교육. 3(특별)은 작업을 다시 고른다 · 6 은 기타. */
const BOX_PICKS: Record<number, [string, string | null]> = {
  1: ['WORKER', 'HIRE'],
  2: ['WORKER', 'CHANGE'],
  3: ['SPECIAL', null],
  4: ['WORKER', 'REGULAR'],
  5: ['SUPERVISOR', 'REGULAR'],
  6: ['OTHER', null],
};

/** 양식의 교육 구분 여섯 칸 — 서버 카탈로그가 아직 옛 판이면 이 글로 그린다(카탈로그 boxes 와 같다). */
const DEFAULT_BOXES = [
  '1. 신규 채용자 교육 (1시간)',
  '2. 작업내용 변경시 교육 (1시간)',
  '3. 특별안전교육 (2시간)',
  '4. 근로자 정기교육 (2시간)',
  '5. 관리감독자 교육 (년16시간)',
  '6. 기      타',
];

const WEEKDAYS = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

/** 「2026년 5월 18일 월요일」 — 양식의 교육 일시 글. */
function longDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return `${Number(match[1])}년 ${Number(match[2])}월 ${Number(match[3])}일 ${WEEKDAYS[date.getDay()]}`;
}

/**
 * 안전교육일지 — 회사 양식 한 장(DOC-122 §16 · 서버 {@code educationPaper} 와 같은 칸). 종류마다 결재 칸 이름 ·
 * 교육 구분의 ■ 자리 · 6번 칸 이름이 다르다. 결재란은 확정 규격(서명칸 30px · 이름표 배경 통일).
 * {@code edit} 를 주면 칸마다 바로 쓴다. 교육 종류는 종이 위의 고르개가 정한다(종이 안의 ■ 는 결과다).
 */
function EducationSheet({
  journal,
  photos,
  edit,
  tools,
  missing,
  approvals,
}: {
  journal: Omit<JournalInput, 'siteId'>;
  photos: JournalPhoto[];
  edit?: PaperEdit;
  tools?: EducationTools;
  missing?: ReadonlySet<RequiredField>;
  approvals?: Approvals;
}) {
  /** 그 칸이 비어 있어 저장이 막혔으면 빨간 테두리. */
  const req = (field: RequiredField, style: CSSProperties): CSSProperties => (missing?.has(field) ? { ...style, ...MISSING } : style);
  const sub = tools?.sub ?? null;
  const boxes = tools?.boxes?.length === 6 ? tools.boxes : DEFAULT_BOXES;
  const signers = sub?.signers?.length === 2 ? sub.signers : ['안전관리자', '소장'];
  const box = boxOf(journal.educationKind, journal.educationSubKind, sub?.box);
  const mid: CSSProperties = { ...td, verticalAlign: 'middle', height: '22px' };
  const label: CSSProperties = { ...th, verticalAlign: 'middle' };
  const center: CSSProperties = { ...mid, textAlign: 'center' };
  const text = (v: string) => (v === '' ? null : v);

  /**
   * 6번 칸의 글 — 적은 글(title)이 먼저, 없으면 그 교육의 이름(MSDS교육 · 성희롱예방교육 …). 종이에서 바로 고친다
   * (사용자 지시 2026-10-23 「6 기타 칸에 MSDS도 넣을 수 있어야」). 비어 있으면 양식의 「기 타 (   )」.
   */
  const sixthText = journal.title ?? (box === 6 ? (sub?.boxLabel ?? null) : null);
  const sixth = () => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ flex: 'none' }}>6.</span>
      {!sixthText && !edit ? <span>기 타 (</span> : null}
      <span style={{ flex: 1, minWidth: 0 }}>
        <Ink value={sixthText} onChange={edit ? (v) => edit({ title: text(v) }) : undefined} />
      </span>
      {!sixthText && !edit ? <span>)</span> : null}
    </span>
  );
  /** 교육 구분 칸을 누르면 그 교육으로 — 고르개와 같은 길(기본값 채우기)을 탄다. 6 은 이미 6 이면 그대로. */
  const pickBox = (n: number) => {
    if (!tools?.choose || n === box) return;
    const [k, s] = BOX_PICKS[n]!;
    tools.choose(k, s);
  };

  return (
    <FillPage>
      {(extra) => (
    <>
      {/*
        머리(제목 · 결재)와 그 아래 칸이 같은 세로선 위에 선다 — 회사 양식의 선 자리를 잰 한 칸 나눔(EDU_COLS)을
        두 표가 같이 쓴다(사용자 지시 2026-10-23 「실제 양식처럼 width 라인을 맞춰」).
        세로선: 교육 일시 | 날짜 · 장소 | 교육시간 | 날씨 | 맑음 ‖ 결재 · 교육대상 | … | 안전관리자 | 소장.
      */}
      <table style={table}>
        <EduCols />
        <tbody>
          <tr>
            <td style={{ ...center, fontSize: '20pt', fontWeight: 700, letterSpacing: '0.4em', height: '60px' }} rowSpan={2} colSpan={5}>
              안 전 교 육 일 지
            </td>
            <th style={label} rowSpan={2}>
              결<br />재
            </th>
            <th style={label} colSpan={2}>
              {signers[0]}
            </th>
            <th style={label}>{signers[1]}</th>
          </tr>
          <tr>
            <td style={signCell} colSpan={2}>
              <HeadSign tools={roleTools(approvals, signers, 0)} />
            </td>
            <td style={signCell}>
              <HeadSign tools={roleTools(approvals, signers, 1)} />
            </td>
          </tr>
        </tbody>
      </table>
      <table style={table}>
        <EduCols />
        <tbody>
          <tr>
            <th style={label}>교육 일시</th>
            <td style={req('journalDate', center)} colSpan={2}>
              {longDate(journal.journalDate)}
            </td>
            <td style={{ ...center, borderRight: 'none', whiteSpace: 'nowrap' }}>날씨 :</td>
            <td style={{ ...center, borderLeft: 'none' }}>
              <Ink value={journal.weather} onChange={edit ? (v) => edit({ weather: text(v) }) : undefined} align="center" />
            </td>
            <th style={label} rowSpan={2} colSpan={2}>
              교육
              <br />
              대상
            </th>
            <td style={req('target', center)} rowSpan={2} colSpan={2}>
              <Ink value={journal.target} onChange={edit ? (v) => edit({ target: text(v) }) : undefined} multiline={!!edit} align="center" />
            </td>
          </tr>
          <tr>
            <th style={label}>교육 장소</th>
            <td style={req('place', center)}>
              <Ink value={journal.place} onChange={edit ? (v) => edit({ place: text(v) }) : undefined} align="center" />
            </td>
            <th style={label}>교육시간</th>
            <td style={req('educationHours', center)} colSpan={2}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
                <span style={{ width: edit ? '48px' : undefined }}>
                  <Ink
                    value={journal.educationHours}
                    onChange={edit ? (v) => edit({ educationHours: v === '' ? null : Number(v) }) : undefined}
                    number
                    align="center"
                  />
                </span>
                {journal.educationHours !== null || edit ? '시간' : ''}
              </span>
            </td>
          </tr>
          <tr>
            <th style={label}>교육 인원</th>
            <td style={req('headcount', center)}>
              <Ink value={journal.headcount} onChange={edit ? (v) => edit({ headcount: v === '' ? null : Number(v) }) : undefined} number align="center" />
            </td>
            <td style={center}>명</td>
            <th style={label} colSpan={2}>
              교육 강사
            </th>
            <td style={req('instructor', center)} colSpan={4}>
              <Ink value={journal.instructor} onChange={edit ? (v) => edit({ instructor: text(v) }) : undefined} align="center" />
            </td>
          </tr>
        </tbody>
      </table>
      <table style={table}>
        <colgroup>
          <col style={{ width: '12%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '40%' }} />
          <col style={{ width: '4%' }} />
          <col />
        </colgroup>
        <tbody>
          {[0, 1, 2].map((r) => (
            <tr key={r}>
              {r === 0 && (
                <th style={label} rowSpan={3}>
                  교육 구분
                </th>
              )}
              {[1, 2].map((c) => {
                const n = r * 2 + c;
                return (
                  <Fragment key={n}>
                    <td
                      className={edit && tools?.choose ? 'sj-mark' : undefined}
                      role={edit ? 'radio' : undefined}
                      aria-checked={edit ? n === box : undefined}
                      tabIndex={edit ? 0 : undefined}
                      style={{ ...center, borderRight: 'none' }}
                      onClick={edit ? () => pickBox(n) : undefined}
                      onKeyDown={
                        edit
                          ? (e) => {
                              if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                pickBox(n);
                              }
                            }
                          : undefined
                      }
                    >
                      {n === box ? '■' : '□'}
                    </td>
                    <td style={{ ...mid, borderLeft: 'none' }}>{n === 6 ? sixth() : boxes[n - 1]}</td>
                  </Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <table style={table}>
        <colgroup>
          <col style={{ width: '12%' }} />
          <col />
        </colgroup>
        <tbody>
          <tr>
            <th style={label}>교육 내용</th>
            <td style={{ ...td, whiteSpace: 'pre-wrap', minHeight: '120px' }}>
              <Ink value={journal.content} onChange={edit ? (v) => edit({ content: text(v) }) : undefined} multiline minHeight={200} />
            </td>
          </tr>
          <tr>
            <th style={label}>
              교육 사진
              {/* 올리기 단추는 이름 칸 아래 — 사진 칸에 띄우면 사진을 가린다. */}
              {edit && tools?.photoUpload && <div style={{ marginTop: '8px' }}>{tools.photoUpload}</div>}
            </th>
            <td style={{ ...td, height: `${300 + extra}px`, verticalAlign: 'top' }}>
              {photos.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {photos.map((photo) => (
                    <div key={photo.id} style={{ textAlign: 'center' }}>
                      {photo.url && <img src={photo.url} alt="" style={{ maxWidth: '100%', maxHeight: '140px', margin: '0 auto' }} />}
                      <div style={{ fontSize: '8pt', color: '#555' }}>{photo.caption ?? ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </td>
          </tr>
          <tr>
            <th style={label}>
              교육자료
              <br />및<br />
              보조기기
            </th>
            <td style={{ ...td, whiteSpace: 'pre-wrap', height: '40px' }}>
              <Ink value={journal.educationMaterial} onChange={edit ? (v) => edit({ educationMaterial: text(v) }) : undefined} multiline minHeight={36} />
            </td>
          </tr>
          <tr>
            <th style={label}>기타 사항</th>
            <td style={{ ...td, whiteSpace: 'pre-wrap' }}>
              <Ink value={journal.specialNote} onChange={edit ? (v) => edit({ specialNote: text(v) }) : undefined} multiline={!!edit} />
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '6px' }}>
        <span>안전이 의사결정의 최우선이다</span>
        <span>㈜보미건설</span>
      </div>
    </>
      )}
    </FillPage>
  );
}

/** 저장 전에 채워야 하는 칸(안전교육일지). 편집 창이 비어 있는 것을 골라 넘긴다. */
export type RequiredField = 'journalDate' | 'educationHours' | 'target' | 'headcount' | 'place' | 'instructor';

/**
 * 비어 있는 [필수] 칸의 표시 — 칸 안쪽의 옅은 붉은 바탕과 부드러운 붉은 선. 표 선(border-collapse)을 건드리지 않게
 * 안쪽 그림자로 긋는다. 인쇄 · PDF 에는 없다(저장이 막혔을 때 작성 창에서만).
 */
const MISSING: CSSProperties = {
  background: 'rgba(254, 226, 226, 0.55)',
  boxShadow: 'inset 0 0 0 1.5px rgba(239, 68, 68, 0.85), inset 0 0 6px rgba(239, 68, 68, 0.25)',
  transition: 'background 0.2s, box-shadow 0.2s',
};

/** 종이에 바로 쓸 때 고칠 값 — 편집 창의 patch 와 같은 꼴이다. 없으면 읽기 전용 미리보기. */
export type PaperEdit = (next: Partial<Omit<JournalInput, 'siteId'>>) => void;

/**
 * 종이의 칸 하나 (DOC-122 §17 · 사용자 결정 「깨끗한 종이 + 누르면 그 칸만 편집」).
 *
 * <p>평소에는 글자만 — 인쇄본과 똑같다. 쓸 수 있는 칸은 올리면 옅은 테두리가 생기고, 누르거나 Tab 으로 오면 그 칸만
 * 입력칸으로 열린다. 입력칸은 칸 폭 그대로이고 글이 길면 줄을 바꿔 아래로 늘어난다(한 줄 칸도 — 종이처럼 접혀 보이게).
 * 한 줄 칸은 Enter 로 닫고, 여러 줄 칸(교육 내용 · 공지 · 특이사항)은 Enter 가 줄바꿈이다. 칸을 떠나면 닫힌다.
 */
function Ink({
  value,
  onChange,
  multiline,
  number,
  align,
  minHeight,
}: {
  value: string | number | null | undefined;
  onChange?: (value: string) => void;
  multiline?: boolean;
  number?: boolean;
  align?: 'center';
  minHeight?: number;
}) {
  const [editing, setEditing] = useState(false);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const ink = useRef<HTMLSpanElement | null>(null);
  const shown = value === null || value === undefined ? '' : String(value);

  // 글 길이만큼 높이를 맞춘다 — 스크롤 막대가 생기지 않게. 여러 줄 칸은 그 칸(td) 안쪽 높이까지 채운다 —
  // 칸은 표 배치로 크게 늘어나 있는데 입력칸이 글 몇 줄 높이로만 열리면 넓은 칸에 짧은 상자가 뜬다(2026-11-04).
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.max(el.scrollHeight, multiline ? cellInnerHeight(el) : 0)}px`;
  }, [editing, shown, multiline]);

  // 칸 아래쪽 빈 곳을 눌러도 열린다 — 글자 영역을 칸 높이로 키우면 칸이 그만큼 또 늘어 서로 키우는 고리가 생긴다(2026-11-04 실측
  // 812 → 1526px). 그래서 크기는 두고, 칸(td) 자체를 누른 것만 받는다.
  useEffect(() => {
    const cell = ink.current?.closest('td');
    if (!cell || !multiline || !onChange || editing) return;
    const open = (event: MouseEvent) => {
      if (event.target !== cell) return;
      event.preventDefault();
      setEditing(true);
    };
    const previous = cell.style.cursor;
    cell.style.cursor = 'text';
    cell.addEventListener('mousedown', open);
    return () => {
      cell.removeEventListener('mousedown', open);
      cell.style.cursor = previous;
    };
  }, [multiline, onChange, editing]);

  if (!onChange) return <>{shown}</>;
  const box: CSSProperties = {
    display: 'block',
    minHeight: minHeight ? `${minHeight}px` : '1.45em',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    textAlign: align,
  };
  if (!editing) {
    return (
      <span
        ref={ink}
        className="sj-ink"
        tabIndex={0}
        style={box}
        onFocus={() => setEditing(true)}
        // 브라우저의 기본 포커스 이동을 막는다 — 막지 않으면 눌린 칸(지금 사라지는 글자)으로 포커스가 가며 막 열린 입력칸이 닫힌다.
        onMouseDown={(e) => {
          e.preventDefault();
          setEditing(true);
        }}
      >
        {shown}
      </span>
    );
  }
  const close = () => setEditing(false);
  if (number) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        className="sj-ink-edit"
        style={{ ...box, width: '100%' }}
        value={shown}
        onChange={(e) => onChange(e.target.value)}
        onBlur={close}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
        }}
      />
    );
  }
  return (
    <textarea
      ref={(el) => {
        area.current = el;
      }}
      autoFocus
      rows={1}
      className="sj-ink-edit"
      style={{ ...box, width: '100%' }}
      value={shown}
      onFocus={(e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
      onChange={(e) => onChange(e.target.value)}
      onBlur={close}
      onKeyDown={(e) => {
        if (e.key === 'Escape' || (!multiline && e.key === 'Enter' && !e.nativeEvent.isComposing)) {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** 칸(td)의 안쪽 높이 — 위아래 여백을 뺀 값. 칸 밖이면 0. */
function cellInnerHeight(el: HTMLElement): number {
  const cell = el.closest('td');
  if (!cell) return 0;
  const style = window.getComputedStyle(cell);
  return Math.max(0, cell.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom));
}

/** 쓸 수 있는 칸의 표시 — 평소엔 없고, 올리거나 열었을 때만. 종이 한 장에 한 번 심는다. */
const INK_STYLES = `
.sj-ink { cursor: text; border-radius: 2px; outline: 1px solid transparent; outline-offset: 1px; }
.sj-ink:hover, .sj-ink:focus { outline-color: rgba(37, 99, 235, 0.55); background: rgba(37, 99, 235, 0.05); }
.sj-ink-edit { border: none; outline: 2px solid #2563eb; outline-offset: 1px; border-radius: 2px; background: #fff; font: inherit; color: inherit; padding: 0; resize: none; overflow: hidden; }
.sj-mark { cursor: pointer; }
.sj-rowtools { position: absolute; right: -34px; bottom: 0; display: flex; flex-direction: column; gap: 2px; }
.sj-rowtools button, .sj-row-x { width: 22px; height: 22px; padding: 0; border-radius: 6px; border: 1px solid #cbd5e1; background: #fff; color: #475569; font-size: 13px; line-height: 20px; cursor: pointer; }
.sj-rowtools button:hover { border-color: #2563eb; color: #2563eb; }
.sj-row-x { position: absolute; right: -34px; bottom: 2px; opacity: 0; font-size: 11px; color: #94a3b8; }
.sj-row:hover .sj-row-x { opacity: 1; }
.sj-row-x:hover { border-color: #dc2626; color: #dc2626; }
.sj-mark:hover, .sj-mark:focus { background: rgba(37, 99, 235, 0.08); outline: none; }
`;

/**
 * 안전보건일지 — 「07. 안전보건일지.xlsx」 세 장(안전보건일지 · T.B.M · 사진대지 · DOC-122 §12). 서버 틀
 * ({@code healthPaper})과 같은 칸 · 같은 빈 줄 수다. 결재란은 확정 규격(서명칸 30px · 이름표 배경 통일).
 * 위험요인 · 안전조치 표는 양식에 없어 줄이 있을 때만 1장 끝에 싣는다(§9 결정).
 *
 * <p>{@code edit} 를 주면 칸마다 바로 쓴다(§13 · 사용자 지시 2026-10-23 「그냥 바로 여기에 입력하게」). 줄은 저장 목록의
 * 차례 그대로다 — n 번째 줄에 쓰면 목록의 n 번째가 된다. 빈 줄은 서버가 버린다. 쓰는 중에는 마지막 줄 아래 한 줄이 더 선다.
 * 양식에 없는 위험요인 표도 쓸 때는 1장 끝에 서고 종이에서 바로 쓴다(§27).
 */
function HealthSheet({
  journal,
  siteName,
  photos,
  edit,
  photoUpload,
  approvals,
}: {
  journal: Omit<JournalInput, 'siteId'>;
  siteName: string;
  photos: JournalPhoto[];
  edit?: PaperEdit;
  /** 사진대지의 [＋ 사진] — 편집 창이 그려 넘긴다. */
  photoUpload?: ReactNode;
  approvals?: Approvals;
}) {
  const mid: CSSProperties = { ...td, verticalAlign: 'middle' };
  const label: CSSProperties = { ...th, verticalAlign: 'middle' };
  const line: CSSProperties = { ...mid, height: '18px' };
  const sec: CSSProperties = { fontWeight: 700, margin: '8px 0 3px' };
  const box = (min: number): CSSProperties => ({ ...note, minHeight: `${min}px` });
  const text = (value: string) => (value === '' ? null : value);
  const count = (value: string) => (value === '' || Number.isNaN(Number(value)) ? null : Number(value));
  /** 줄 수 — 양식의 빈 줄 수 이상, 쓰는 중이면 마지막으로 쓴 줄 다음 한 줄까지. */
  const rowsOf = (filled: boolean[], min: number) => {
    const last = filled.lastIndexOf(true) + 1;
    return Math.max(min, edit ? last + 1 : last);
  };

  const head = journal.patrolNotes.filter((row) => row.patrolKind === 'HEAD');
  const safe = journal.patrolNotes.filter((row) => row.patrolKind === 'SAFE');
  /** n 번째 줄에 쓰면 목록의 n 번째가 되도록 빈 줄을 채워 둔다 — 빈 줄은 서버가 버린다. */
  const padTo = <T,>(list: T[], at: number, blank: () => T) => {
    const next = [...list];
    while (next.length <= at) next.push(blank());
    return next;
  };
  const setNote = (kind: PatrolKind, at: number, key: 'inspector' | 'instruction' | 'result', value: string) => {
    const blank = (): PatrolNote => ({ patrolKind: kind, inspector: null, instruction: null, result: null });
    const mine = padTo(kind === 'HEAD' ? head : safe, at, blank);
    mine[at] = { ...mine[at]!, [key]: text(value) };
    edit?.({ patrolNotes: kind === 'HEAD' ? [...mine, ...safe] : [...head, ...mine] });
  };
  const setActivity = (at: number, key: 'activity' | 'meeting', value: string) => {
    const next = padTo(journal.activities, at, (): Activity => ({ activity: null, meeting: null }));
    next[at] = { ...next[at]!, [key]: text(value) };
    edit?.({ activities: next });
  };
  const setFacility = (at: number, key: 'facilityKind' | 'location' | 'workers' | 'detail', value: string) => {
    const next = padTo(journal.facilities, at, (): Facility => ({ facilityKind: null, location: null, workers: null, detail: null }));
    next[at] = { ...next[at]!, [key]: key === 'workers' ? count(value) : text(value) };
    edit?.({ facilities: next });
  };
  const t = journal.tbm;
  const setTbm = (key: keyof NonNullable<typeof t>, value: string, numeric = false) =>
    edit?.({ tbm: { ...(t ?? blankTbm()), [key]: numeric ? count(value) : text(value) } });
  const people = (key: 'workforceOwn' | 'workforceVendor' | 'amOwn' | 'amVendor' | 'pmOwn' | 'pmVendor') => (
    <td style={{ ...mid, textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
        <span style={{ width: edit ? '48px' : undefined }}>
          <Ink value={t?.[key]} onChange={edit ? (v) => setTbm(key, v, true) : undefined} number align="center" />
        </span>
        명
      </div>
    </td>
  );

  /**
   * 표마다 빈 줄 수 — 양식의 줄 수에서 시작해 종이 오른쪽 여백의 [＋] · [−] 로 늘리고 줄인다(사용자 지시 2026-10-23
   * 「각 항목들 늘리거나 줄일 수 있어야」). 적은 줄 아래로는 줄지 않는다. 소장 Patrol 은 한 칸이라 줄 대신 높이(줄 단위)다.
   */
  // 줄 수는 일지에 둔다(V551) — 저장하면 빈 줄이라도 그대로 다시 열리고 PDF 도 같은 줄 수다(사용자 지시 2026-10-23).
  const mins: Record<RowTable, number> = { ...DEFAULT_ROWS, ...(journal.rowLayout ?? {}) };
  /** 지금 보이는 줄 수에서 하나 더 · 하나 덜 — 적은 줄이 양식 줄 수보다 많아도 [＋] 가 바로 한 줄을 더한다. */
  const grow = (key: RowTable, by: 1 | -1, current: number) => edit?.({ rowLayout: { ...mins, [key]: Math.max(1, current + by) } });
  const safeRows = rowsOf(safe.map((row) => !!(row.inspector?.trim() || row.instruction?.trim() || row.result?.trim())), mins.safe);
  const activityRows = rowsOf(journal.activities.map((row) => !!row.activity?.trim()), mins.activity);
  const meetingRows = rowsOf(journal.activities.map((row) => !!row.meeting?.trim()), mins.meeting);
  const facilityRows = rowsOf(
    journal.facilities.map((row) => !!(row.facilityKind?.trim() || row.location?.trim() || row.detail?.trim())),
    mins.facility,
  );
  /** 소장 Patrol — 한 칸에 많이 쓴다(회사 양식은 다섯 줄이 병합된 칸). 옛 일지의 여러 줄은 줄바꿈으로 이어 보인다. */
  const headText = (key: 'instruction' | 'result') =>
    head
      .map((row) => row[key]?.trim())
      .filter(Boolean)
      .join('\n') || null;
  const setHead = (key: 'instruction' | 'result', value: string) => {
    const merged: PatrolNote = {
      patrolKind: 'HEAD',
      inspector: null,
      instruction: key === 'instruction' ? text(value) : headText('instruction'),
      result: key === 'result' ? text(value) : headText('result'),
    };
    edit?.({ patrolNotes: [merged, ...safe] });
  };
  /** 줄 지우기 — 그 줄을 빼고 아래 줄이 올라온다. 행사 · 회의는 한 목록의 두 칸이라 그 칸만 당긴다. */
  const dropSafe = (at: number) => edit?.({ patrolNotes: [...head, ...safe.filter((_, i) => i !== at)] });
  const dropFacility = (at: number) => edit?.({ facilities: journal.facilities.filter((_, i) => i !== at) });
  const dropActivity = (at: number, key: 'activity' | 'meeting') => {
    const other = key === 'activity' ? 'meeting' : 'activity';
    const mine = journal.activities.map((row) => row[key]).filter((_, i) => i !== at);
    const theirs = journal.activities.map((row) => row[other]);
    const length = Math.max(mine.length, theirs.length);
    edit?.({
      activities: Array.from({ length }, (_, i) => ({ activity: null, meeting: null, [key]: mine[i] ?? null, [other]: theirs[i] ?? null }) as Activity),
    });
  };
  const rowX = (onDrop: () => void) =>
    edit ? (
      <button type="button" className="sj-row-x" aria-label="줄 지우기" onClick={onDrop}>
        ✕
      </button>
    ) : null;
  const hasItems = journal.healthItems.some((row) => row.workContent?.trim() || row.hazard?.trim() || row.measure?.trim());
  const range = (n: number) => Array.from({ length: n }, (_, i) => i);

  return (
    <>
      <FillPage>
        {(extra) => (
          <>
      {/* ── 1장 · 안전보건일지 */}
      {/*
        머리(제목 · 결재) · 현장명 줄 · 소장 Patrol 이 같은 세로선 위에 선다 — 회사 양식(07. 안전보건일지.xlsx)의 선 자리를
        잰 한 칸 나눔(HEALTH_COLS). 소장 Patrol 의 가운데 선은 작성일 칸의 오른쪽 선이다.
      */}
      <table style={table}>
        <HealthCols />
        <tbody>
          <tr>
            <td style={{ ...mid, textAlign: 'center', fontSize: '22pt', fontWeight: 700, letterSpacing: '0.3em' }} rowSpan={2} colSpan={4}>
              안전보건일지
            </td>
            <th style={label} rowSpan={2}>
              결<br />재
            </th>
            <th style={label} colSpan={2}>
              안전관리자
            </th>
            <th style={label} colSpan={2}>
              현장소장
            </th>
          </tr>
          <tr>
            <td style={signCell} colSpan={2}>
              <HeadSign tools={approvals?.MANAGER} />
            </td>
            <td style={signCell} colSpan={2}>
              <HeadSign tools={approvals?.HEAD} />
            </td>
          </tr>
        </tbody>
      </table>
      <table style={table}>
        <HealthCols />
        <tbody>
          <tr>
            <th style={label}>현장명</th>
            <td style={mid}>{siteName}</td>
            <th style={label}>작성일</th>
            <td style={{ ...mid, textAlign: 'center', whiteSpace: 'nowrap' }} colSpan={3}>
              {longDate(journal.journalDate)}
            </td>
            <th style={label} colSpan={2}>
              날씨
            </th>
            <td style={{ ...mid, textAlign: 'center' }}>
              <Ink value={journal.weather} onChange={edit ? (v) => edit({ weather: text(v) }) : undefined} align="center" />
            </td>
          </tr>
        </tbody>
      </table>
      <div>
      <table style={table}>
        <HealthCols />
        <tbody>
          <tr>
            <th style={label} colSpan={3}>
              소장 Patrol 점검 및 지시(전달) 사항
            </th>
            <th style={label} colSpan={6}>
              조치결과(FeedBack)
            </th>
          </tr>
          <tr>
            <td style={{ ...td, height: `${mins.head * ROW_PX}px`, whiteSpace: 'pre-wrap' }} colSpan={3}>
              <Ink value={headText('instruction')} onChange={edit ? (v) => setHead('instruction', v) : undefined} multiline minHeight={mins.head * ROW_PX - 10} />
            </td>
            <td style={{ ...td, height: `${mins.head * ROW_PX}px`, whiteSpace: 'pre-wrap' }} colSpan={6}>
              <Ink value={headText('result')} onChange={edit ? (v) => setHead('result', v) : undefined} multiline minHeight={mins.head * ROW_PX - 10} />
            </td>
          </tr>
        </tbody>
      </table>
      <RowTools edit={!!edit} onMore={() => grow('head', 1, mins.head)} onLess={() => grow('head', -1, mins.head)} />
      </div>
      <div>
      <div style={sec}>■ 안전 Patrol점검 현황</div>
      <table style={table}>
        <tbody>
          <tr>
            <th style={{ ...label, width: '12%' }}>점검자</th>
            <th style={{ ...label, width: '60%' }}>지도, 지시, 점검 내용</th>
            <th style={label}>조치결과</th>
          </tr>
          {range(safeRows).map((at) => (
            <tr key={at} className="sj-row">
              <td style={{ ...line, textAlign: 'center' }}>
                <Ink value={safe[at]?.inspector} onChange={edit ? (v) => setNote('SAFE', at, 'inspector', v) : undefined} align="center" />
              </td>
              <td style={line}>
                <Ink value={safe[at]?.instruction} onChange={edit ? (v) => setNote('SAFE', at, 'instruction', v) : undefined} />
              </td>
              <td style={{ ...line, position: 'relative' }}>
                <Ink value={safe[at]?.result} onChange={edit ? (v) => setNote('SAFE', at, 'result', v) : undefined} />
                {at < safe.length && rowX(() => dropSafe(at))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <RowTools edit={!!edit} onMore={() => grow('safe', 1, safeRows)} onLess={() => grow('safe', -1, safeRows)} />
      </div>
      {(
        [
          ['■ 행사 및 활동(교육,합동점검,행사 등)', 'activity', activityRows],
          ['■ 안전공정 회의내용(직원회의 공지사항)', 'meeting', meetingRows],
        ] as const
      ).map(([title, key, rows]) => (
        <div key={title}>
          <div style={sec}>{title}</div>
          <table style={table}>
            <tbody>
              {range(rows).map((at) => (
                <tr key={at} className="sj-row">
                  <td style={{ ...line, position: 'relative' }}>
                    <Ink value={journal.activities[at]?.[key]} onChange={edit ? (v) => setActivity(at, key, v) : undefined} />
                    {!!journal.activities[at]?.[key]?.trim() && rowX(() => dropActivity(at, key))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <RowTools edit={!!edit} onMore={() => grow(key, 1, rows)} onLess={() => grow(key, -1, rows)} />
        </div>
      ))}
      {/* 덩어리 — 장 끝에 걸치면 제목 · 표 · 손잡이가 같이 다음 장으로(FillPage). */}
      <div>
      <div style={sec}>■ 안전시설물 설치현황</div>
      <table style={table}>
        <tbody>
          <tr>
            <th style={{ ...label, width: '19%' }}>시설물 종류</th>
            <th style={{ ...label, width: '23%' }}>설치장소</th>
            <th style={{ ...label, width: '11%' }}>투입인원</th>
            <th style={label}>설치내용</th>
          </tr>
          {range(facilityRows).map((at) => {
            const row = journal.facilities[at];
            return (
              <tr key={at} className="sj-row">
                <td style={line}>
                  <Ink value={row?.facilityKind} onChange={edit ? (v) => setFacility(at, 'facilityKind', v) : undefined} />
                </td>
                <td style={line}>
                  <Ink value={row?.location} onChange={edit ? (v) => setFacility(at, 'location', v) : undefined} />
                </td>
                <td style={{ ...line, textAlign: 'center' }}>
                  {edit ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <span style={{ width: '40px' }}>
                        <Ink value={row?.workers} onChange={(v) => setFacility(at, 'workers', v)} number align="center" />
                      </span>
                      명
                    </div>
                  ) : row?.workers == null ? (
                    ''
                  ) : (
                    `${row.workers} 명`
                  )}
                </td>
                <td style={{ ...line, position: 'relative' }}>
                  <Ink value={row?.detail} onChange={edit ? (v) => setFacility(at, 'detail', v) : undefined} />
                  {at < journal.facilities.length && rowX(() => dropFacility(at))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <RowTools edit={!!edit} onMore={() => grow('facility', 1, facilityRows)} onLess={() => grow('facility', -1, facilityRows)} />
      </div>
      {(hasItems || edit) && (
        <HazardLines
          journal={journal}
          edit={edit}
          rowsOf={rowsOf}
          min={mins.hazard}
          onGrow={(by, current) => grow('hazard', by, current)}
          line={line}
          label={label}
          sec={sec}
          text={text}
        />
      )}
      <div style={{ height: `${extra}px` }} />
      <Slogan />
          </>
        )}
      </FillPage>

      {/* ── 2장 · T.B.M — 공통 공지사항 칸이 늘어난다 */}
      <PageBreak />
      <FillPage>
        {(extra) => (
          <>
      <div style={{ textAlign: 'center', fontSize: '22pt', fontWeight: 700, marginBottom: '8px' }}>T.B.M</div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>현장명 : {siteName}</span>
        <span>{journal.journalDate}</span>
      </div>
      {(
        [
          ['■ 공통 공지사항', 'notice', 260],
          ['■ 1일 1사고사례 교육내용', 'accidentCase', 100],
          ['■ 주요공종 협력사 주의사항', 'vendorCaution', 170],
        ] as const
      ).map(([title, key, min]) => (
        <div key={key}>
          <div style={sec}>{title}</div>
          <div style={box(key === 'notice' ? min + extra : min)}>
            <Ink value={t?.[key]} onChange={edit ? (v) => setTbm(key, v) : undefined} multiline minHeight={min - 14} />
          </div>
        </div>
      ))}
      <div style={sec}>■ TBM 출력인원(출력인원과 참석인원과 상이할 경우 사유 기재)</div>
      <table style={table}>
        <colgroup>
          <col style={{ width: '18%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '18%' }} />
          <col />
        </colgroup>
        <tbody>
          <tr>
            <th style={label}>출력인원</th>
            <th style={label}>보미건설</th>
            {people('workforceOwn')}
            <th style={label}>협력업체</th>
            {people('workforceVendor')}
          </tr>
          <tr>
            <th style={label} rowSpan={2}>
              TBM 참가인원
            </th>
            <th style={label}>오전 참가인원</th>
            {people('amOwn')}
            <th style={label}>오전 참가인원</th>
            {people('amVendor')}
          </tr>
          <tr>
            <th style={label}>오후 참가인원</th>
            {people('pmOwn')}
            <th style={label}>오후 참가인원</th>
            {people('pmVendor')}
          </tr>
          {(
            [
              ['오전', 'absentAm'],
              ['오후', 'absentPm'],
            ] as const
          ).map(([when, key], at) => (
            <tr key={key}>
              {at === 0 && (
                <th style={label} rowSpan={2}>
                  TBM 미참석 사유
                </th>
              )}
              <td style={mid} colSpan={4}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <span style={{ flex: 'none' }}>{when} :</span>
                  {/* 줄의 남은 폭을 다 쓴다 — 없으면 빈 칸이 0 폭으로 접혀 누를 데가 없다. */}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <Ink value={t?.[key]} onChange={edit ? (v) => setTbm(key, v) : undefined} />
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Slogan />
          </>
        )}
      </FillPage>

      {/*
        ── 3장~ · 사진대지(사용자 지시 2026-10-23 「TBM 양식에 똑같이 하지 말고 교육사진 탭처럼 그냥 사진대지」).
        오전 · 오후 TBM 자리 없이 한 표의 큰 칸에 사진을 둘씩 놓는다 — 한 장에 여섯, 넘으면 장이 더 붙는다.
        올린 사진(자리 있던 옛 TBM 사진 포함)이 모두 올린 차례로 실린다. 서버 healthPaper 와 같은 칸 · 같은 쪽 나눔.
      */}
      {photoPages(photos).map((page, at) => (
        <div key={at}>
          <PageBreak />
          <FillPage>
            {(extra) => (
              <>
          <div style={{ textAlign: 'center', fontSize: '22pt', fontWeight: 700, letterSpacing: '0.5em', marginBottom: '8px' }}>사진대지</div>
          <table style={table}>
            <colgroup>
              <col style={{ width: '12%' }} />
              <col />
              <col style={{ width: '12%' }} />
              <col style={{ width: '22%' }} />
            </colgroup>
            <tbody>
              <tr>
                <th style={label}>현장명</th>
                <td style={mid}>{siteName}</td>
                <th style={label}>작성일</th>
                <td style={{ ...mid, textAlign: 'center' }}>{journal.journalDate}</td>
              </tr>
              <tr>
                <th style={label}>
                  사진
                  {at === 0 && edit && photoUpload && <div style={{ marginTop: '8px' }}>{photoUpload}</div>}
                </th>
                <td style={{ ...td, height: `${600 + extra}px`, verticalAlign: 'top' }} colSpan={3}>
                  {page.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      {page.map((photo) => (
                        <div key={photo.id} style={{ textAlign: 'center' }}>
                          {photo.url && <img src={photo.url} alt="" style={{ maxWidth: '100%', maxHeight: '220px', margin: '0 auto' }} />}
                          <div style={{ fontSize: '8pt', color: '#555', marginTop: '2px' }}>{photo.caption ?? ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          <Slogan />
              </>
            )}
          </FillPage>
        </div>
      ))}
    </>
  );
}

/** 안전보건일지 사진대지 한 장의 사진 수(세 줄 × 둘) — 서버 HEALTH_SHEET_PHOTOS 와 같다. 사진이 없어도 빈 한 장은 선다(양식). */
function photoPages(photos: JournalPhoto[]): JournalPhoto[][] {
  const pages: JournalPhoto[][] = [];
  for (let i = 0; i < photos.length; i += 6) pages.push(photos.slice(i, i + 6));
  return pages.length > 0 ? pages : [[]];
}

/** 안전교육일지 「교육 사진」 칸에 드는 사진 수(두 줄 × 둘). 넘는 사진은 사진대지 장으로 간다 — 서버 EDU_CELL_PHOTOS 와 같다. */
const EDU_CELL_PHOTOS = 4;

/**
 * 사진대지 장 — 양식 안에 자리가 없는 사진을 한 장에 둘씩 같은 표(사진 · 현 장 명 · 위 치 · 사진내용)로 싣는다
 * (사용자 지시 2026-10-23 「사진대지가 많으면 사진대지 해서 표를 추가」 · 「이미지도 많아지면 페이지가 추가될 수 있잖아」).
 * 세 일지가 같이 쓴다 — 안전보건일지의 그 밖의 사진 · 교육 사진 칸을 넘친 사진 · 순회점검일지의 사진. 서버
 * {@code photoSheets} 와 같은 차례 · 같은 쪽 나눔이다. 위치 칸은 비어 있다(사진마다 위치 자료가 없다 — 손으로 적는 칸).
 */
function PhotoSheets({ photos, siteName }: { photos: JournalPhoto[]; siteName: string }) {
  const mid: CSSProperties = { ...td, verticalAlign: 'middle' };
  const label: CSSProperties = { ...th, verticalAlign: 'middle' };
  const pages: JournalPhoto[][] = [];
  for (let i = 0; i < photos.length; i += 2) pages.push(photos.slice(i, i + 2));
  return (
    <>
      {pages.map((pair, page) => (
        <div key={page}>
          <PageBreak />
          <div style={{ textAlign: 'center', fontSize: '22pt', fontWeight: 700, letterSpacing: '0.5em', marginBottom: '8px' }}>사진대지</div>
          {pair.map((photo) => (
            <table key={photo.id} style={{ ...table, marginTop: '8px' }}>
              <tbody>
                <tr>
                  <td style={{ ...mid, height: '300px', textAlign: 'center' }} colSpan={4}>
                    {photo.url && <img src={photo.url} alt="" style={{ maxHeight: '290px', maxWidth: '100%', margin: '0 auto' }} />}
                  </td>
                </tr>
                <tr>
                  <th style={{ ...label, width: '18%' }}>현 장 명</th>
                  <td style={mid}>{siteName}</td>
                  <th style={{ ...label, width: '12%' }}>위 치</th>
                  <td style={{ ...mid, width: '28%' }} />
                </tr>
                <tr>
                  <th style={label}>사진내용</th>
                  <td style={mid} colSpan={3}>
                    {photo.caption ?? ''}
                  </td>
                </tr>
              </tbody>
            </table>
          ))}
          <Slogan />
        </div>
      ))}
    </>
  );
}

/** 결재란 두 칸의 승인 손잡이 — 소장 칸(HEAD) · 다른 칸(MANAGER). */
export type Approvals = { HEAD: HeadApprovalTools; MANAGER: HeadApprovalTools };

/** 결재 칸 차례 → 그 칸의 손잡이. 이름에 「소장」 이 든 칸이 HEAD, 나머지가 MANAGER. */
function roleTools(approvals: Approvals | undefined, signers: string[], at: number): HeadApprovalTools | undefined {
  if (!approvals) return undefined;
  return at === headCellOf(signers) ? approvals.HEAD : approvals.MANAGER;
}

/** 결재 서명 칸 — 확정 규격 30px. 승인 서명 · 날짜는 가운데. */
const signCell: CSSProperties = { border: '0.6pt solid #444', height: '30px', textAlign: 'center', verticalAlign: 'middle', padding: '2px' };

/** 결재란 「현장소장」 승인의 값과 손잡이(편집 창이 넘긴다). */
export type HeadApprovalTools = {
  value: HeadApproval | null;
  approve?: () => void;
  cancel?: () => void;
  busy?: boolean;
};

/**
 * 결재란 「현장소장」 칸(V548) — 승인했으면 그 순간의 전자결재 서명(없으면 이름)과 날짜, 아니면 [승인].
 * 승인한 칸에 올리면 [취소] 가 뜬다. 인쇄본 · PDF 에는 서명 · 날짜만 남는다.
 */
export function HeadSign({ tools }: { tools?: HeadApprovalTools }) {
  const value = tools?.value ?? null;
  const [hover, setHover] = useState(false);
  const small: CSSProperties = { fontSize: '11px', padding: '2px 10px', borderRadius: '6px', cursor: 'pointer' };
  if (!value) {
    if (!tools?.approve) return null;
    return (
      <button
        type="button"
        disabled={tools.busy}
        onClick={tools.approve}
        style={{ ...small, border: '1px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
      >
        {tools.busy ? '…' : '승인'}
      </button>
    );
  }
  const day = value.approvedAt.slice(0, 10);
  return (
    <div style={{ position: 'relative', lineHeight: 1.1 }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {value.signImage ? (
        <img src={value.signImage} alt={value.approverName ?? ''} style={{ maxHeight: '24px', maxWidth: '100%', margin: '0 auto', display: 'block' }} />
      ) : (
        <b>{value.approverName}</b>
      )}
      <div style={{ fontSize: '7pt', color: '#555' }}>{day}</div>
      {hover && tools?.cancel && (
        <button
          type="button"
          disabled={tools.busy}
          onClick={tools.cancel}
          style={{ ...small, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', border: '1px solid #cbd5e1', background: '#fff' }}
        >
          취소
        </button>
      )}
    </div>
  );
}

/**
 * 작업별 위험요인 · 안전조치 — 양식에 없는 표라 줄이 있을 때만 1장 끝에 싣는다(§9). 쓸 때는 늘 서고 종이에서 바로
 * 쓴다(사용자 지시 2026-10-23 「안전보건일지 화면 제대로 작업하게」 — 왼쪽 격자를 걷었다). 이행 칸은 누르면 「확인」.
 */
function HazardLines({
  journal,
  edit,
  rowsOf,
  min,
  onGrow,
  line,
  label,
  sec,
  text,
}: {
  journal: Omit<JournalInput, 'siteId'>;
  edit?: PaperEdit;
  rowsOf: (filled: boolean[], min: number) => number;
  /** 쓸 때 빈 줄 수 — 다른 1장 표처럼 [＋] · [−] 로 늘리고 줄인다. */
  min: number;
  onGrow: (by: 1 | -1, current: number) => void;
  line: CSSProperties;
  label: CSSProperties;
  sec: CSSProperties;
  text: (value: string) => string | null;
}) {
  const items = journal.healthItems;
  const filled = (row: HealthItem | undefined) => !!(row?.workContent?.trim() || row?.hazard?.trim() || row?.measure?.trim() || row?.location?.trim() || row?.note?.trim());
  const count = rowsOf(items.map((row) => filled(row)), edit ? min : 0);
  const shown = edit ? count : items.filter((row) => filled(row)).length;
  const setItem = (at: number, next: Partial<HealthItem>) => {
    const list = [...items];
    while (list.length <= at) list.push({ workContent: null, location: null, hazard: null, measure: null, checked: false, note: null });
    list[at] = { ...list[at]!, ...next };
    edit?.({ healthItems: list });
  };
  const rows = edit ? Array.from({ length: shown }, (_, i) => items[i]) : items.filter((row) => filled(row));
  const cellInk = (at: number, key: 'workContent' | 'location' | 'hazard' | 'measure' | 'note', row: HealthItem | undefined) => (
    <td style={line}>
      <Ink value={row?.[key]} onChange={edit ? (v) => setItem(at, { [key]: text(v) }) : undefined} />
    </td>
  );
  return (
    <div>
      <div style={sec}>■ 작업별 위험요인 · 안전조치</div>
      <table style={table}>
        <tbody>
          <tr>
            <th style={{ ...label, width: '6%' }}>No</th>
            <th style={{ ...label, width: '19%' }}>작업내용</th>
            <th style={{ ...label, width: '11%' }}>위치</th>
            <th style={{ ...label, width: '21%' }}>위험요인</th>
            <th style={{ ...label, width: '21%' }}>안전조치</th>
            <th style={{ ...label, width: '8%' }}>이행</th>
            <th style={label}>비고</th>
          </tr>
          {rows.map((row, at) => (
            <tr key={at} className="sj-row">
              <td style={{ ...line, textAlign: 'center' }}>{filled(row) ? at + 1 : ''}</td>
              {cellInk(at, 'workContent', row)}
              {cellInk(at, 'location', row)}
              {cellInk(at, 'hazard', row)}
              {cellInk(at, 'measure', row)}
              <td
                className={edit ? 'sj-mark' : undefined}
                role={edit ? 'checkbox' : undefined}
                aria-checked={edit ? !!row?.checked : undefined}
                tabIndex={edit ? 0 : undefined}
                style={{ ...line, textAlign: 'center' }}
                onClick={edit ? () => setItem(at, { checked: !row?.checked }) : undefined}
                onKeyDown={
                  edit
                    ? (e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          setItem(at, { checked: !row?.checked });
                        }
                      }
                    : undefined
                }
              >
                {row?.checked ? '확인' : ''}
              </td>
              <td style={{ ...line, position: 'relative' }}>
                <Ink value={row?.note} onChange={edit ? (v) => setItem(at, { note: text(v) }) : undefined} />
                {edit && at < items.length && (
                  <button
                    type="button"
                    className="sj-row-x"
                    aria-label="줄 지우기"
                    onClick={() => edit({ healthItems: items.filter((_, i) => i !== at) })}
                  >
                    ✕
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <RowTools edit={!!edit} onMore={() => onGrow(1, shown)} onLess={() => onGrow(-1, shown)} />
    </div>
  );
}

/** 양식마다 쪽 끝에 찍힌 글. */
function Slogan() {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '10px' }}>
      <span>&quot;안전은 모두의 의무이고, 책임이다&quot;</span>
      <span>㈜보미건설</span>
    </div>
  );
}

/**
 * 안전교육일지 머리의 세로선 — 회사 양식(교육일지 HWP)에서 잰 자리(748 기준: 90 · 270 · 355 · 410 · 512 · 548 · 580 · 648).
 * 교육 일시 | 날짜(2) | 날씨 | 맑음 ‖ 결재 | 교육대상(2) · 안전관리자(2) | 소장. 서버 educationPaper 와 같은 값.
 */
const EDU_COL_WIDTHS = [12.03, 24.06, 11.36, 7.35, 13.64, 4.81, 4.28, 9.09, 13.38];

/** 안전보건일지 머리의 세로선 — 양식(665 기준: 85 · 320 · 385 · 470 · 497 · 553 · 581 · 610). 서버 healthPaper 와 같은 값. */
const HEALTH_COL_WIDTHS = [12.78, 35.34, 9.77, 12.78, 4.06, 8.42, 4.21, 4.36, 8.28];

function EduCols() {
  return (
    <colgroup>
      {EDU_COL_WIDTHS.map((w, i) => (
        <col key={i} style={{ width: `${w}%` }} />
      ))}
    </colgroup>
  );
}

function HealthCols() {
  return (
    <colgroup>
      {HEALTH_COL_WIDTHS.map((w, i) => (
        <col key={i} style={{ width: `${w}%` }} />
      ))}
    </colgroup>
  );
}

/** 안전보건일지 1장 표들 — 줄 수를 늘리고 줄이는 단위. */
type RowTable = 'head' | 'safe' | 'activity' | 'meeting' | 'facility' | 'hazard';

/** 양식(07. 안전보건일지.xlsx)의 빈 줄 수 — 서버 healthPaper 와 같다. 소장 Patrol 은 병합 칸의 높이(줄 단위). */
const DEFAULT_ROWS: Record<RowTable, number> = { head: 5, safe: 7, activity: 6, meeting: 5, facility: 3, hazard: 1 };

/** 한 줄 높이(px) — 소장 Patrol 병합 칸의 높이를 줄 단위로 잴 때. */
const ROW_PX = 28;

/**
 * 표 바로 아래 오른쪽 여백의 [＋] · [−] — 빈 줄을 늘리고 줄인다. 쓸 때만 서고 인쇄본에는 없다. 표 사이 간격을
 * 바꾸지 않도록 높이 0 의 자리에 떠 있다.
 */
function RowTools({ edit, onMore, onLess }: { edit: boolean; onMore: () => void; onLess: () => void }) {
  if (!edit) return null;
  return (
    <div style={{ position: 'relative', height: 0 }}>
      <div className="sj-rowtools">
        <button type="button" aria-label="줄 늘리기" onClick={onMore}>
          ＋
        </button>
        <button type="button" aria-label="줄 줄이기" onClick={onLess}>
          −
        </button>
      </div>
    </div>
  );
}

/** A4 한 장의 글 자리 높이(mm) — 297 에서 위아래 여백 15mm 씩(종이 padding · PageBreak 여백과 같다). */
const PAGE_BODY_MM = 267;

/**
 * 쪽 하나를 A4 장으로 나눠 그린다(사용자 지시 2026-10-23 「1페이지 이내면 1페이지 꽉 채우도록」 · 「+ 를 계속 눌렀는데
 * 페이지가 늘어났어야 하는데 1페이지에 묶여 있잖아, 동적으로」 · 「해당하는 컴포넌트를 밑으로 내려」). 한 장을 넘으면 장 끝에
 * 걸치는 덩어리(바로 아래 자식 — 제목 · 표 · 손잡이를 묶은 div)를 통째로 다음 장 첫머리로 내리고 장 사이 틈을 그리며, 마지막
 * 장도 한 장 끝까지 잡는다. 한 장보다 큰 덩어리만 줄 단위로 나눈다. 아래는 한 장 안일 때의 동작이다.
 *
 * 한 장 안에 끝나는 쪽을 A4 한 장 높이까지 채운다.
 * 모자란 만큼(px)을 {@code children(extra)} 로 넘기고, 쪽마다 정한 칸(순회 특이사항 · 교육 사진 · T.B.M 공통 공지사항 ·
 * 사진대지 사진 칸 · 안전보건 1장은 표어 위 빈자리)이 그만큼 늘어난다. 한 장을 넘는 쪽은 0 — 줄이지 않는다.
 * 늘린 높이를 빼고 잰 「제 높이」 로 계산해 늘리고 나서 다시 재도 값이 흔들리지 않는다.
 */
function FillPage({ children }: { children: (extra: number) => ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  /** 장 수만큼 높이를 잡는 바깥 상자 — 높이는 여기에 둔다. 안(ref)의 크기를 보고 있어야 줄을 더할 때마다 다시 잰다. */
  const box = useRef<HTMLDivElement | null>(null);
  const applied = useRef(0);
  /** 다음 장으로 민 덩어리 · 칸 — 다시 잴 때 먼저 되돌린다. */
  const pushed = useRef<Array<{ node: HTMLElement; prop: 'paddingTop' | 'marginTop'; value: string }>>([]);
  const [extra, setExtra] = useState(0);
  /** 장 사이 — 한 장을 넘을 때만. */
  const [breaks, setBreaks] = useState<PageGap[]>([]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const px = 96 / 25.4;
      const pageH = PAGE_BODY_MM * px;
      // 장 사이: 아래 여백 15mm + 띠 18px + 위 여백 15mm(PageBreak 와 같은 모양).
      const gap = 30 * px + BAND_PX;
      for (const one of pushed.current) one.node.style[one.prop] = one.value;
      pushed.current = [];
      if (box.current) box.current.style.minHeight = '';
      const natural = el.offsetHeight - applied.current;

      // 한 장 안 — 모자란 만큼 늘어날 칸에 준다.
      if (natural <= pageH + 1) {
        const next = Math.max(0, Math.floor(pageH - natural));
        if (Math.abs(next - applied.current) > 1) {
          applied.current = next;
          setExtra(next);
        }
        setBreaks((prev) => (prev.length ? [] : prev));
        return;
      }
      // 한 장을 넘는다 — 늘린 칸을 거두고 다시 잰다.
      if (applied.current !== 0) {
        applied.current = 0;
        setExtra(0);
        return;
      }

      // 덩어리(이 쪽의 바로 아래 자식 — ■ 제목 · 표 · 손잡이를 묶은 div, 머리 표, 표어)를 위에서부터 보며, 장 끝을 걸치거나
      // 장 사이에 떨어지는 덩어리를 통째로 다음 장 첫머리로 내린다(사용자 지시 2026-10-23 「해당하는 컴포넌트를 밑으로 내려.
      // 컴포넌트 하나 전체가 A4 를 넘어가지 않는 이상」). 덩어리 하나가 한 장보다 크면 그 안의 줄 단위로 나눈다.
      const top0 = el.getBoundingClientRect().top;
      const measureOf = (node: HTMLElement) => {
        const r = node.getBoundingClientRect();
        return { node, top: r.top - top0, height: r.height };
      };
      const blocks = Array.from(el.children as HTMLCollectionOf<HTMLElement>).map(measureOf).filter((one) => one.height >= 2);
      const gaps: PageGap[] = [];
      let shift = 0;
      let limit = pageH;
      const push = (node: HTMLElement, by: number) => {
        const prop = node.tagName === 'TABLE' ? 'marginTop' : 'paddingTop';
        const value = node.style[prop];
        const base = parseFloat(getComputedStyle(node)[prop]) || 0;
        node.style[prop] = `${base + by}px`;
        pushed.current.push({ node, prop, value });
      };
      /** 이 자리(top)·높이의 것이 장 끝에 걸치는가 · 장 사이에 떨어지는가. 걸치면 내릴 만큼(px). */
      const overflow = (top: number, height: number) => {
        while (top >= limit + gap) limit += pageH + gap;
        const crosses = top < limit && top + height > limit + 1;
        const inGap = top >= limit && top < limit + gap;
        return (crosses || inGap) && top > 0 ? limit + gap - top : 0;
      };
      for (const block of blocks) {
        const top = block.top + shift;
        if (block.height <= pageH) {
          const by = overflow(top, block.height);
          if (by > 0) {
            push(block.node, by);
            shift += by;
            limit += pageH + gap;
          }
          continue;
        }
        // 한 장보다 큰 덩어리 — 그 안의 줄(표 안의 표는 빼고)을 장 끝에서 나눈다. 민 자리는 종이색으로 덮고 다음 장 첫 줄에 윗선.
        const rows = Array.from(block.node.querySelectorAll<HTMLTableRowElement>('tr'))
          .filter((tr) => !tr.parentElement?.closest('td'))
          .map(measureOf)
          .filter((one) => one.height >= 2);
        for (const row of rows) {
          const rowTop = row.top + shift;
          if (row.height > pageH) continue;
          const by = overflow(rowTop, row.height);
          if (by > 0) {
            for (const cell of Array.from(row.node.children as HTMLCollectionOf<HTMLElement>)) push(cell, by);
            gaps.push({ top: Math.round(rowTop + 1), height: Math.round(by - 1), band: Math.round(limit + 15 * px - rowTop - 1), line: true });
            shift += by;
            limit += pageH + gap;
          }
        }
      }
      // 마지막 장도 한 장 끝까지 — 장 수만큼 높이를 잡고, 민 줄이 없는 장 사이에도 틈을 그린다.
      const total = el.offsetHeight;
      const pages = Math.max(1, Math.ceil((total + gap) / (pageH + gap) - 0.001));
      if (box.current) box.current.style.minHeight = `${pages * pageH + (pages - 1) * gap}px`;
      for (let i = 1; i < pages; i++) {
        const at = i * pageH + (i - 1) * gap;
        if (!gaps.some((one) => Math.abs(one.top + one.height - (at + gap)) < 3)) {
          gaps.push({ top: Math.round(at), height: Math.round(gap), band: Math.round(15 * px), line: false });
        }
      }
      gaps.sort((a, b) => a.top - b.top);
      setBreaks((prev) => (JSON.stringify(prev) === JSON.stringify(gaps) ? prev : gaps));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={box} style={{ position: 'relative' }}>
      <div ref={ref}>{children(extra)}</div>
      {breaks.map((one) => (
        <div
          key={one.top}
          aria-hidden
          style={{ position: 'absolute', top: `${one.top}px`, height: `${one.height}px`, left: '-14mm', right: '-14mm', background: '#fff', pointerEvents: 'none' }}
        >
          <div style={{ ...bandStyle, position: 'absolute', left: 0, right: 0, top: `${one.band}px` }} />
          {one.line && <div style={{ position: 'absolute', left: '14mm', right: '14mm', bottom: 0, borderTop: '0.6pt solid #444' }} />}
        </div>
      ))}
    </div>
  );
}

/**
 * 장 사이 한 곳 — 앞 장 끝 ~ 다음 장 첫머리를 종이색으로 덮고 그 안에 띠를 그린다.
 *
 * @param top 덮기 시작하는 자리(px, 이 쪽의 위에서) · height 덮는 높이
 * @param band 덮개 안에서 띠의 자리 · line 다음 장 첫 줄의 윗선을 그을지(표 줄을 밀었을 때)
 */
type PageGap = { top: number; height: number; band: number; line: boolean };

/** 장 사이 띠의 높이(px) — PageBreak 와 같은 모양. */
const BAND_PX = 18;

const bandStyle: CSSProperties = {
  height: `${BAND_PX}px`,
  background: 'var(--bg-muted, #e8edf3)',
  boxShadow: 'inset 0 4px 4px -3px rgba(0,0,0,0.18), inset 0 -4px 4px -3px rgba(0,0,0,0.18)',
};

/**
 * 미리보기의 쪽 나눔 — PDF 의 page-break 자리. 한 장이 끝나고 다음 장이 시작되는 모양으로 그린다: 종이 아래 여백 →
 * 종이 사이 틈(뒤 바탕색 · 위아래 장의 가장자리 그림자) → 다음 장 위 여백. 여백은 종이 안쪽 여백(15mm)과 같다.
 */
function PageBreak() {
  return (
    <div
      aria-hidden
      style={{
        margin: '15mm -14mm',
        height: '18px',
        background: 'var(--bg-muted, #e8edf3)',
        boxShadow: 'inset 0 4px 4px -3px rgba(0,0,0,0.18), inset 0 -4px 4px -3px rgba(0,0,0,0.18)',
      }}
    />
  );
}

function Photos({ label, photos }: { label: string; photos: JournalPhoto[] }) {
  return (
    <Section label={label}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {photos.map((photo) => (
          <div key={photo.id}>
            {photo.url && <img src={photo.url} alt="" style={{ width: '100%', display: 'block' }} />}
            <div style={{ fontSize: '8pt', color: '#555', marginTop: '2px' }}>{photo.caption ?? ''}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <h2 style={subheading}>{label}</h2>
      {children}
    </>
  );
}

const paper: CSSProperties = {
  width: '210mm',
  minHeight: '297mm',
  padding: '15mm 14mm',
  boxSizing: 'border-box',
  background: '#fff',
  color: '#111',
  fontSize: '9.5pt',
  lineHeight: 1.45,
  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
};
const heading: CSSProperties = { fontSize: '17pt', fontWeight: 700, textAlign: 'center', letterSpacing: '0.3em', margin: '0 0 10px 0' };
const subheading: CSSProperties = { fontSize: '11pt', fontWeight: 700, margin: '12px 0 4px 0' };
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' };
const cell: CSSProperties = { border: '0.6pt solid #444', padding: '4px 5px', verticalAlign: 'top', wordBreak: 'break-all' };
const th: CSSProperties = { ...cell, background: '#f1f1f1', fontWeight: 400, textAlign: 'center' };
const td: CSSProperties = cell;
const note: CSSProperties = { border: '0.6pt solid #444', padding: '6px', minHeight: '40px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' };
